import { useElementSize } from '@mantine/hooks';
import sortBy from 'lodash.sortby';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import constants from '../../../../constants';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const AGE_BUCKETS = ['0-7 days', '8-21 days', '22+ days'];
const AGE_COLORS = ['#22c55e', '#eab308', '#ef4444'];

const buildAgingConfig = (data, agencyKey) => {
	const now = Date.now();
	const counts = {};
	const agencies = new Set();

	data.forEach((row) => {
		const agency = row[agencyKey];
		if (!agency) return;
		const released = new Date(row.released_on).getTime();
		const end = row.completed_on ? new Date(row.completed_on).getTime() : now;
		const ageDays = (end - released) / MS_PER_DAY;

		let bucket;
		if (ageDays <= 7) bucket = AGE_BUCKETS[0];
		else if (ageDays <= 21) bucket = AGE_BUCKETS[1];
		else bucket = AGE_BUCKETS[2];

		agencies.add(agency);
		counts[bucket] = counts[bucket] || {};
		counts[bucket][agency] = (counts[bucket][agency] || 0) + 1;
	});

	const totals = {};
	agencies.forEach((a) => {
		totals[a] = AGE_BUCKETS.reduce(
			(sum, b) => sum + ((counts[b] && counts[b][a]) || 0),
			0,
		);
	});
	const categories = sortBy([...agencies], (a) => totals[a]);

	const series = AGE_BUCKETS.map((bucket) => ({
		name: bucket,
		data: categories.map((a) => (counts[bucket] && counts[bucket][a]) || 0),
	}));

	return { categories, series };
};

const isClosedFilter = (a) =>
	a.length === constants.closedStatuses.length &&
	a.every((s) => constants.closedStatuses.includes(s));

const TopReferringOrgs = ({ data = [], outgoing = false, statuses = [] }) => {
	const { ref, width } = useElementSize();
	const agencyKey = outgoing ? 'rec_agency_name' : 'ref_agency_name2';

	if (statuses.length > 0 && isClosedFilter(statuses)) {
		const counts = {};
		data.forEach((row) => {
			const agency = row[agencyKey];
			if (!agency) return;
			if (!statuses.includes(row.request_status)) return;
			if (!counts[agency]) counts[agency] = { completed: 0, other: 0 };
			if (row.request_status === 'completed') {
				counts[agency].completed += 1;
			} else {
				counts[agency].other += 1;
			}
		});
		const categories = sortBy(
			Object.keys(counts),
			(a) => counts[a].completed + counts[a].other,
		);
		const series = [
			{
				name: 'Service Provided',
				data: categories.map((a) => counts[a].completed),
			},
			{
				name: 'Service Not Provided',
				data: categories.map((a) => counts[a].other),
			},
		];
		const height = Math.max(categories.length * 75, 120);
		return (
			<div ref={ref}>
				<div className="text-xl">Top Referring Orgs</div>
				{categories.length === 0 ? (
					<div className="mt-4 text-center text-gray-500">
						No Referring Org data available
					</div>
				) : (
					<Chart
						type="bar"
						width={width}
						height={height}
						options={{
							colors: ['#22c55e', '#ef4444'],
							plotOptions: { bar: { horizontal: true } },
							xaxis: {
								categories,
								labels: {
									formatter: function (value) {
										return Number(value).toFixed(0);
									},
								},
							},
							legend: { position: 'top' },
							tooltip: {
								y: {
									formatter: (v) => `${v} referral${v === 1 ? '' : 's'}`,
								},
							},
						}}
						series={series}
					/>
				)}
			</div>
		);
	} else if (statuses.length > 0) {
		const filteredData = data.filter((row) =>
			statuses.includes(row.request_status),
		);
		const { categories, series } = buildAgingConfig(filteredData, agencyKey);
		const height = Math.max(categories.length * 50, 120);
		return (
			<div ref={ref}>
				<div className="text-xl">Top Referring Orgs</div>
				{categories.length === 0 ? (
					<div className="mt-4 text-center text-gray-500">
						No Referring Org data available
					</div>
				) : (
					<Chart
						type="bar"
						width={width}
						height={height}
						options={{
							chart: { stacked: true },
							colors: AGE_COLORS,
							plotOptions: { bar: { horizontal: true } },
							xaxis: {
								categories,
								labels: {
									formatter: function (value) {
										return Number(value).toFixed(0);
									},
								},
							},
							legend: { position: 'top' },
							tooltip: {
								y: {
									formatter: (v) => `${v} referral${v === 1 ? '' : 's'}`,
								},
							},
						}}
						series={series}
					/>
				)}
			</div>
		);
	}

	const chartData = data.reduce((prev, row) => {
		if (!prev[row[agencyKey]]) {
			prev[row[agencyKey]] = 1;
		} else {
			prev[row[agencyKey]]++;
		}
		return prev;
	}, {});
	const mappedData = sortBy(
		Object.entries(chartData).map(([category, count]) => ({
			category,
			count,
		})),
		['count'],
	).reverse();
	const seriesData = mappedData.map(({ count }) => count);
	const height = Math.max(mappedData.length * 50, 120);
	let tickAmount = 5;
	if (Math.max(...seriesData) < 5) {
		tickAmount = Math.max(...seriesData);
	}
	return (
		<div ref={ref}>
			<div className="text-xl">Top Referring Orgs</div>
			{mappedData.length === 0 ? (
				<div className="mt-4 text-center text-gray-500">
					No Referring Org data available
				</div>
			) : (
				<Chart
					type="bar"
					width={width}
					height={height}
					options={{
						colors: ['#3abddb'],
						xaxis: {
							tickAmount,
							labels: {
								formatter: function (value) {
									return value.toFixed(0);
								},
							},
						},
						plotOptions: { bar: { horizontal: true } },
					}}
					series={[
						{
							name: 'Count',
							data: mappedData,
							parsing: { y: 'count', x: 'category' },
						},
					]}
				/>
			)}
		</div>
	);
};

TopReferringOrgs.propTypes = {
	data: PropTypes.array,
	outgoing: PropTypes.bool,
	statuses: PropTypes.arrayOf(PropTypes.string),
};

export default TopReferringOrgs;
